import { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { IEmployee } from 'models';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { useCreateEmployeeMutation, useFetchEmployeeQuery, useUpdateEmployeeMutation } from 'store/apis';

interface UseEmployeeFormManagerProps {
  employeeId?: number | null;
  next?: (data?: any) => void;
}

export const useEmployeeFormManager = ({ employeeId = null, next = () => {} }: UseEmployeeFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [skipFetchEmployee, setSkipFetchEmployee] = useState<boolean>(true);
  const { data: employee, isLoading: loading_form } = useFetchEmployeeQuery(
    { orgSlug, id: employeeId as number },
    { skip: skipFetchEmployee },
  );
  const useMutation = employee ? useUpdateEmployeeMutation : useCreateEmployeeMutation;
  const [mutate, { isLoading, isSuccess, isError, ...rest }] = useMutation();

  useEffect(() => {
    if (employeeId) {
      setSkipFetchEmployee(false);
    }
  }, [employeeId]);

  const initialValues = {
    name: employee?.name || '',
    code: employee?.code || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
  };

  const validationSchema = Yup.object({
    name: Yup.string().nullable().required('Name is required.'),
    code: Yup.string().nullable().required('Code is required.'),
    email: Yup.string().nullable().required('Email is required.').email('Invalid email address'),
    phone: Yup.string().nullable().required('Phone number is required.'),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      let mutateProps: any = {
        orgSlug,
        employee: values,
      };

      if (employee) {
        mutateProps = { ...mutateProps, id: employee.id };
      }

      mutate(mutateProps);
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;

      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [
    {
      employee,
      formManager,
      loading_form,
      loading_submit: isLoading,
    },
  ] as [
    {
      employee: IEmployee | null;
      formManager: FormikProps<typeof initialValues>;
      loading_form: boolean;
      loading_submit: boolean;
    },
  ];
};
